import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { COMPANY, EQUIPMENT_TYPES } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  phone: "",
  mc_number: "",
  equipment_type: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.equipment_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        mc_number: form.mc_number.trim() || null,
        equipment_type: form.equipment_type,
        message: form.message.trim(),
      });
      setSubmitted(true);
      setForm(initialState);
      toast.success("Thanks — we'll be in touch within one business day.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please call us at " + COMPANY.phone;
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 border-t border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-6">
              Contact
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F2A44]">
              Tell us about
              <br />
              <span className="italic">your operation.</span>
            </h2>
            <p className="mt-8 text-[#1A1A1A]/75 leading-relaxed max-w-md">
              Send a few details and we'll be in touch within one business day —
              usually much sooner. If you'd rather talk, give us a call.
            </p>

            <dl className="mt-12 space-y-6">
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-2">
                  Phone
                </dt>
                <dd>
                  <a
                    href={COMPANY.phoneHref}
                    data-testid="contact-phone-link"
                    className="font-serif text-2xl text-[#0F2A44] border-b-2 border-[#D4AF7F] pb-1 hover:text-[#D4AF7F] transition-colors"
                  >
                    {COMPANY.phone}
                  </a>
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-2">
                  Email
                </dt>
                <dd>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    data-testid="contact-email-link"
                    className="text-base text-[#0F2A44] hover:text-[#D4AF7F] transition-colors"
                  >
                    {COMPANY.email}
                  </a>
                </dd>
              </div>
            </dl>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-[#D4AF7F] bg-white p-10 lg:p-12"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF7F] mb-6">
                  Message received
                </p>
                <h3 className="font-serif text-3xl text-[#0F2A44] mb-4 leading-tight">
                  Thanks — we got it.
                </h3>
                <p className="text-[#1A1A1A]/75 leading-relaxed mb-8">
                  A dispatcher will reach out within one business day. If you need
                  to talk sooner, call {COMPANY.phone}.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-send-another"
                  className="text-sm tracking-wide text-[#0F2A44] border-b-2 border-[#D4AF7F] pb-1 hover:text-[#D4AF7F] transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="space-y-8"
                noValidate
              >
                <FormField
                  id="name"
                  name="name"
                  label="Full name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  testId="contact-input-name"
                  autoComplete="name"
                />
                <FormField
                  id="phone"
                  name="phone"
                  type="tel"
                  label="Phone"
                  required
                  value={form.phone}
                  onChange={handleChange}
                  testId="contact-input-phone"
                  autoComplete="tel"
                />
                <FormField
                  id="mc_number"
                  name="mc_number"
                  label="MC number"
                  optional
                  value={form.mc_number}
                  onChange={handleChange}
                  testId="contact-input-mc"
                />

                <div>
                  <label
                    htmlFor="equipment_type"
                    className="block text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3"
                  >
                    Equipment type <span className="text-[#D4AF7F]">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="equipment_type"
                      name="equipment_type"
                      required
                      value={form.equipment_type}
                      onChange={handleChange}
                      data-testid="contact-input-equipment"
                      className="w-full appearance-none bg-transparent border-b border-[#E5E7EB] focus:border-[#0F2A44] focus:outline-none py-3 pr-8 text-base text-[#1A1A1A] transition-colors"
                    >
                      <option value="">Select equipment…</option>
                      {EQUIPMENT_TYPES.map((eq) => (
                        <option key={eq} value={eq}>
                          {eq}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#6B7280]">
                      ▾
                    </span>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3"
                  >
                    Message <span className="text-[#D4AF7F]">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={handleChange}
                    data-testid="contact-input-message"
                    placeholder="Tell us about your truck, lanes, and what you're looking for."
                    className="w-full bg-transparent border-b border-[#E5E7EB] focus:border-[#0F2A44] focus:outline-none py-3 text-base text-[#1A1A1A] placeholder:text-[#6B7280]/60 transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit-button"
                  className="inline-flex items-center gap-3 bg-[#0F2A44] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#0F2A44]/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending…" : "Submit Inquiry"}
                  {!submitting && (
                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                  )}
                </button>

                <p className="text-xs text-[#6B7280] leading-relaxed">
                  By submitting, you agree to be contacted by Crownpath about
                  dispatching services. We never sell or share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const FormField = ({
  id,
  name,
  label,
  type = "text",
  required,
  optional,
  value,
  onChange,
  testId,
  autoComplete,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-3"
    >
      {label}{" "}
      {required && <span className="text-[#D4AF7F]">*</span>}
      {optional && (
        <span className="text-[#6B7280]/70 normal-case tracking-normal text-xs ml-1">
          (optional)
        </span>
      )}
    </label>
    <input
      id={id}
      name={name}
      type={type}
      required={required}
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="w-full bg-transparent border-b border-[#E5E7EB] focus:border-[#0F2A44] focus:outline-none py-3 text-base text-[#1A1A1A] transition-colors"
    />
  </div>
);
